import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, Save, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  getBarMenuItems,
  seedBarMenuItems,
  setBarMenuItems,
  type BarMenuCategory,
  type StoredBarMenuItem,
} from '@/lib/barMenuItems'
import type { BarMenuItem as FallbackBarMenuItem } from '@/lib/barMenuItems'
import { BarMenuItemForm } from './BarMenuItemForm'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

interface BarMenuItemsManagerProps {
  category: BarMenuCategory
  zonePrefix: string
}

const fallbackKey: Record<BarMenuCategory, string> = {
  breakfast: 'breakfast.items',
  lunch: 'lunch.items',
  aperitifs: 'aperitifs.items',
}

export function BarMenuItemsManager({
  category,
  zonePrefix,
}: BarMenuItemsManagerProps) {
  const { t } = useTranslation('admin')
  const { t: tBar } = useTranslation('bar')
  const [savedItems, setSavedItems] = useState<StoredBarMenuItem[]>(() =>
    getBarMenuItems(category)
  )
  const [draft, setDraft] = useState<StoredBarMenuItem[]>(savedItems)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<StoredBarMenuItem | null>(null)

  const isAperitifs = category === 'aperitifs'

  useEffect(() => {
    const fallback = tBar(fallbackKey[category], {
      returnObjects: true,
    }) as FallbackBarMenuItem[]
    seedBarMenuItems(category, fallback)
    const loaded = getBarMenuItems(category)
    setSavedItems(loaded)
    setDraft(loaded)
  }, [category, tBar])

  const isDirty = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(savedItems)
  }, [draft, savedItems])

  const sortedItems = useMemo(() => {
    return [...draft].sort((a, b) => b.createdAt - a.createdAt)
  }, [draft])

  const handleSaveChanges = () => {
    setBarMenuItems(category, draft)
    const updated = getBarMenuItems(category)
    setSavedItems(updated)
    setDraft(updated)
  }

  const handleDiscardChanges = () => {
    setDraft(savedItems)
  }

  const handleAddToDraft = (
    data: Omit<StoredBarMenuItem, 'id' | 'createdAt' | 'updatedAt' | 'category'> &
      { id?: string }
  ) => {
    if (data.id) {
      setDraft((prev) =>
        prev.map((item) =>
          item.id === data.id
            ? { ...item, ...data, updatedAt: Date.now() }
            : item
        )
      )
    } else {
      const created: StoredBarMenuItem = {
        ...data,
        id: generateId(),
        category,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      setDraft((prev) => [created, ...prev])
    }
  }

  const handleAdd = () => {
    setSelectedItem(null)
    setIsModalOpen(true)
  }

  const handleEdit = (item: StoredBarMenuItem) => {
    setSelectedItem(item)
    setIsModalOpen(true)
  }

  const handleDelete = (id: string) => {
    setDraft((prev) => prev.filter((item) => item.id !== id))
  }

  const handleClose = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-cord bg-white">
        <header className="flex flex-wrap items-center justify-between gap-3 bg-bar-light px-5 py-4">
          <h3 className="font-display text-lg font-bold text-ink">
            {t(`${zonePrefix}.title`)}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={!isDirty}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                isDirty
                  ? 'bg-bar text-white shadow-sm hover:bg-bar-dark'
                  : 'cursor-not-allowed border border-dashed border-muted/40 bg-transparent text-muted/60'
              )}
            >
              <Save className="h-3.5 w-3.5" aria-hidden="true" />
              {t(`${zonePrefix}.saveChanges`)}
            </button>
            <button
              type="button"
              onClick={handleDiscardChanges}
              disabled={!isDirty}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                isDirty
                  ? 'bg-white text-ink shadow-sm hover:bg-cream'
                  : 'cursor-not-allowed border border-dashed border-muted/40 bg-transparent text-muted/60'
              )}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              {t(`${zonePrefix}.discardChanges`)}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-1.5 rounded-full bg-bar px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-colors hover:bg-bar-dark"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t(`${zonePrefix}.addDish`)}
            </button>
          </div>
        </header>

        {sortedItems.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm text-muted">{t(`${zonePrefix}.empty`)}</p>
          </div>
        ) : (
          <>
            <div className="sm:hidden divide-y divide-cord">
              {sortedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-14 w-14 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-lg bg-cord text-[10px] font-medium text-muted">
                      {t(`${zonePrefix}.noPhoto`)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{item.name}</p>
                    {!isAperitifs && (
                      <p className="text-sm text-muted">{item.price || '-'}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      aria-label={t(`${zonePrefix}.editDish`)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/10 text-ink transition-colors hover:bg-bar hover:text-white"
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      aria-label={t(`${zonePrefix}.delete`)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/10 text-ink transition-colors hover:bg-pizza hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-cord text-xs uppercase tracking-wider text-muted">
                  <th className="py-3 pl-5 pr-4 font-semibold">
                    {t(`${zonePrefix}.table.photo`)}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t(`${zonePrefix}.table.name`)}
                  </th>
                  {!isAperitifs && (
                    <>
                      <th className="py-3 pr-4 font-semibold">
                        {t(`${zonePrefix}.table.description`)}
                      </th>
                      <th className="py-3 pr-4 font-semibold">
                        {t(`${zonePrefix}.table.allergens`)}
                      </th>
                      <th className="py-3 pr-4 font-semibold">
                        {t(`${zonePrefix}.table.price`)}
                      </th>
                    </>
                  )}
                  <th className="py-3 pr-5 font-semibold">
                    {t(`${zonePrefix}.table.actions`)}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cord">
                {sortedItems.map((item) => (
                  <tr
                    key={item.id}
                    className="transition-colors hover:bg-cream/60"
                  >
                    <td className="py-3 pl-5 pr-4">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-cord text-[10px] font-medium text-muted">
                          {t(`${zonePrefix}.noPhoto`)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 font-medium text-ink">{item.name}</td>
                    {!isAperitifs && (
                      <>
                        <td className="max-w-xs py-3 pr-4">
                          <p className="truncate text-ink/80">
                            {item.description || '-'}
                          </p>
                        </td>
                        <td className="py-3 pr-4 text-ink/80">
                          {item.allergens || '-'}
                        </td>
                        <td className="py-3 pr-4 font-medium text-ink">
                          {item.price || '-'}
                        </td>
                      </>
                    )}
                    <td className="py-3 pr-5">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          aria-label={t(`${zonePrefix}.editDish`)}
                          className="inline-flex items-center rounded-full bg-ink/10 p-1.5 text-ink transition-colors hover:bg-bar hover:text-white"
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          aria-label={t(`${zonePrefix}.delete`)}
                          className="inline-flex items-center rounded-full bg-ink/10 p-1.5 text-ink transition-colors hover:bg-pizza hover:text-white"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <BarMenuItemForm
        isOpen={isModalOpen}
        zonePrefix={zonePrefix}
        initialItem={selectedItem}
        onClose={handleClose}
        onSave={handleAddToDraft}
      />
    </>
  )
}
